import { Regexp } from '../_models/regexp';

export function isBlank(text: string): boolean {
  return !(text.trim());
}

export function isEmptyObject(obj: object | null): boolean {
  return obj == null || (Object.keys(obj).length === 0);
}

export function removeAccents(text: string): string {
  return text.normalize("NFD").replace(/[\u0300-\u036f]/g, '');
}

export function replaceWhiteChars(text: string, replaceChar: string): string {
  return text.replace(/\s+/g, replaceChar);
}

export function replaceSpecialChars(text: string, replaceChar: string): string {
  return text.replace(/[^0-9a-zA-Z ]/g, replaceChar);
}

export function removeDots(text: string): string {
  return text.replace(/\./g, '');
}

export function copyObject(object: any): any { // src: https://stackoverflow.com/questions/28150967/typescript-cloning-object
  let copy: any;

  // Handle the 3 simple types, and null or undefined
  if (null == object || 'object' !== typeof object) {
    return object;
  }

  // Handle Date
  if (object instanceof Date) {
    copy = new Date();
    copy.setTime(object.getTime());
    return copy;
  }

  // Handle Array
  if (object instanceof Array) {
    copy = [];
    for (let i = 0, len = object.length; i < len; i++) {
      copy[i] = copyObject(object[i]);
    }
    return copy;
  }

  // Handle Object
  if (object instanceof Object) {
    copy = {};
    for (const attr in object) {
      if (object.hasOwnProperty(attr)) {
        copy[attr] = copyObject(object[attr]);
      }
    }
    return copy;
  }

  throw new Error('Unable to copy object! Its type isn\'t supported.');
}

export function sortFn<T>(sortProp: keyof T, asc = true, type: 'text' | 'number' = 'text'): (a: T, b: T) => number {
  const collator = new Intl.Collator();

  if (type === 'text') {
    return asc ? (a: any, b: any) => collator.compare(a[sortProp], b[sortProp])
      : (a: any, b: any) => collator.compare(b[sortProp], a[sortProp]);
  } else {
    return asc ? (a: any, b: any) => a[sortProp] - b[sortProp] : (a: any, b: any) => b[sortProp] - a[sortProp];
  }
}

export function isValidDate(date: Date): boolean {
  if (Object.prototype.toString.call(date) === '[object Date]') {
    return !isNaN(date.getTime());
  }
  return false;
}

export function isValidEmail(email: string): boolean {
  return Regexp.EMAIL.test(email);
}

export function isValidClassId(classId: string): boolean {
  return Regexp.CLASS.test(classId);
}

export function isValidStudentPassword(password: string): boolean {
  return Regexp.STUDENT_PASSWORD.test(password);
}

export function isEmoji(emoji: string | null): boolean {
  //const emojiRegex = /\p{Emoji}/u;
  const emojiRegex = /\p{Extended_Pictographic}/u;
  return emoji != null && emojiRegex.test(emoji);
}

export function formatText(text: string | null | undefined): string {
  if (!text) {
    return '';
  }

  const trimmedText = text.trim();
  if (!trimmedText) {
    return '';
  }

  return trimmedText.toLowerCase();
}

export function generateUniqueId(): string {
  return 'key-' + Date.now() + '-' + Math.floor(Math.random() * 10000);
}

// src: https://stackoverflow.com/questions/6134039/format-number-to-always-show-2-decimal-places
export function round(value: number, decimalPlaces: number): number {
  return Number(Number(Math.round(parseFloat(value + 'e' + decimalPlaces)) + 'e-' + decimalPlaces).toFixed(decimalPlaces));
}

export function addLeadingZeros(num: number, length: number): string {
  return String(num).padStart(length, '0');
}

